import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import { Link, useLocation } from "@tanstack/react-router"
import { cva } from "class-variance-authority"
import type { FC, MouseEvent } from "react"
import { useState } from "react"
import { twMerge } from "tailwind-merge"

import { getCollapsedNavItemTextSx } from "./styles"

import { usePermissionManager } from "@/hooks/usePermissionManager"
import type { RouteNavObject } from "@/types/routes"

const listItemIconContainerStyles = cva("flex h-6 min-w-6 items-center justify-center", {
  variants: {
    selected: {
      true: "rounded bg-primary text-white",
    },
  },
})

const listItemButtonStyles = cva("rounded", {
  variants: {
    selected: {
      true: "bg-white ",
    },
  },
})

const listSubItemButtonStyles = cva("rounded", {
  variants: {
    selected: {
      true: "bg-primary-200 ",
    },
  },
})

interface AdminNavItemProps {
  route: RouteNavObject
  isCollapsed?: boolean
}

const AdminNavItem: FC<AdminNavItemProps> = ({ route, isCollapsed = false }) => {
  const location = useLocation()
  const { hasPermissions } = usePermissionManager()

  const [open, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isAdminMenuSelected = location.pathname.includes(route.pathname)

  const handleClick = () => {
    setOpen(!open)
  }

  const handleCollapsedClick = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
  }

  const handleMenuClose = () => setAnchorEl(null)

  const renderSubItems = (item: RouteNavObject) => {
    const isSelected = location.pathname.includes(item.pathname)

    if (item.permissions && !hasPermissions(item.permissions)) {
      return null
    }

    return (
      <ListItemButton
        key={`admin-nav-${item.pathname}`}
        component={Link}
        to={item.pathname}
        selected={isSelected}
        data-testid={`admin-sub-sidenav-${item.label?.toLowerCase()}`}
        className={twMerge(listSubItemButtonStyles({ selected: isSelected }))}
      >
        <ListItemText className='ml-3' primary={item.label} />
      </ListItemButton>
    )
  }

  return (
    <>
      <Tooltip title={isCollapsed && !anchorEl ? route.label : ""} placement='right'>
        <ListItemButton
          onClick={isCollapsed ? handleCollapsedClick : handleClick}
          data-testid={`sidenav-${route.pathname}`}
          className={twMerge(listItemButtonStyles({ selected: isAdminMenuSelected }))}
          selected={isAdminMenuSelected}
          sx={isCollapsed ? { justifyContent: "center" } : undefined}
        >
          {route.icon && (
            <ListItemIcon className={twMerge(listItemIconContainerStyles({ selected: isAdminMenuSelected }))}>
              {route.icon}
            </ListItemIcon>
          )}
          <ListItemText primary={route.label} sx={getCollapsedNavItemTextSx(isCollapsed)} />
          <Box
            sx={{
              opacity: isCollapsed ? 0 : 1,
              maxWidth: isCollapsed ? 0 : 24,
              overflow: "hidden",
              flexShrink: 0,
              transition: "opacity 0.2s ease, max-width 0.2s ease",
            }}
          >
            {open ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </Box>
        </ListItemButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ ml: 1 }}
      >
        {route.children?.map((child) => {
          if (child.permissions && !hasPermissions(child.permissions)) return null
          const isSelected = location.pathname.includes(child.pathname)
          return (
            <MenuItem
              key={child.pathname}
              component={Link}
              to={child.pathname}
              selected={isSelected}
              onClick={handleMenuClose}
              className='hover:bg-primary-100'
            >
              {child.icon && (
                <ListItemIcon className='flex min-w-6 items-center justify-center'>{child.icon}</ListItemIcon>
              )}
              <Typography className='ml-2.5'>{child.label}</Typography>
            </MenuItem>
          )
        })}
      </Menu>

      {!isCollapsed && (
        <Collapse in={open} timeout='auto' unmountOnExit>
          <List disablePadding>{route.children?.map((child) => renderSubItems(child))}</List>
        </Collapse>
      )}
    </>
  )
}

export default AdminNavItem
